import React from 'react';
import cx from 'classnames';

import styles from './BaseButton.module.scss';
import Touchable, { TouchableProps } from '../../Touchable';
import capitalize from '../../utils/fns/capitalize';

export interface BaseButtonProps
  extends Omit<
    TouchableProps,
    'size' | 'wide' | 'block' | 'centered' | 'inline'
  > {
  /**
   * Specific align with the Button.
   * @default left
   */
  align?: 'left' | 'center';
  /**
   * Specify the `Button` appearance type.
   * @default solid
   */
  appearance?: 'solid' | 'outline' | 'ghost';
  /** The content for `Button`. */
  children: React.ReactNode;
  /**
   * If true, `Button` will be disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Specify the `Button`'s width.
   * @default inline
   */
  display?: 'inline' | 'fixed' | 'full';
  /**
   * Specify the font size within the `Button`. (Inherited from Text component)
   * @default 48
   */
  size?: 56 | 48 | 40 | 32 | 24;
  /**
   * If true, a loading indicator will be visible.
   * @default false
   */
  isLoading?: boolean;
  /**
   * Specify the `Button`'s intent.
   * @default default
   */
  intent?: 'default' | 'destructive';
}

export const BaseButton = ({
  appearance = 'solid',
  className,
  children,
  disabled,
  display = 'inline',
  intent = 'default',
  isLoading = false,
  size = 48,
  ...restProps
}: BaseButtonProps) => {
  return (
    <Touchable
      {...restProps}
      role={'button'}
      className={cx(
        styles.buttonBase,
        isLoading && styles.isLoading,
        disabled && styles.isDisabled,
        appearance && styles[appearance],
        intent && styles[`intent${capitalize(intent)}`],
        display && styles[display],
        size && styles[`size${size}`],
        className,
      )}
      disabled={disabled || isLoading}
      aria-live={disabled || isLoading ? 'polite' : undefined}
      aria-busy={disabled || isLoading ? 'true' : undefined}
      tabIndex={disabled || isLoading ? -1 : undefined}
    >
      {children}
    </Touchable>
  );
};

BaseButton.displayName = 'BaseButton';
export default BaseButton;
export { styles };
